import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HelpPosition, useHelp } from '~/systems/help';
import { assertNever, useDOMNodeSize, useWindowInnerSize } from '~/util';
import { useRaf } from '~/util/animation';
import { useLocalStorageSynchronized } from '~/util/persistence';
import { Rect } from '~/util/rect';
import HelpContent from '../HelpContent/HelpContent';
import type { CSSProperties } from 'react';
import type { HelpScenario } from '~/systems/help';
import type { Size, Vec2 } from '~/util';
import './HelpWindow.css';

export default function HelpWindow() {
  const [helpStorage, setHelpStorage] = useLocalStorageSynchronized(
    'help-state',
    { seen: false }
  );
  const [currentHelpIdx, setCurrentHelpIdx] = useState<number | null>(
    helpStorage.seen ? null : 0
  );
  const help = useHelp();

  const HELP_ID_INIT = 'init';

  const helpScenarios: HelpScenario[] = useMemo(() => {
    const tabSidesV = [
      HelpPosition.Bottom,
      HelpPosition.Top,
      HelpPosition.Left,
      HelpPosition.Right
    ];
    const tabSidesH = [
      HelpPosition.Left,
      HelpPosition.Right,
      HelpPosition.Bottom,
      HelpPosition.Top
    ];

    return [
      {
        id: HELP_ID_INIT,
        position: [HelpPosition.Right, HelpPosition.Bottom],
        mask: false,
        header: 'Welcome to Pulser Studio',
        content: 'If you ever need help, you can open the tutorial.'
      },
      {
        id: 'tab-Register',
        position: tabSidesH,
        header: 'Start with setting up the register',
        content:
          'The register shows all atoms in the system. You can drag them around.'
      },
      {
        id: 'tab-Channels',
        position: tabSidesV,
        header: 'Customize channels and add pulses',
        content:
          'There are four types of channels: Rydberg local & global and Raman local & global.'
      },
      {
        id: 'controls',
        position: tabSidesH,
        header: 'Advanced parameters panel',
        content: "Allows you to specify parameters' values manually."
      },
      {
        id: 'sim-control',
        position: tabSidesH,
        header: 'Emulation is automatic up to 6 atoms',
        content:
          "Press 'Emulate' if there are more than 6 atoms in the register. No emulation above 12 atoms."
      },
      {
        id: 'tab-QuantumState',
        position: tabSidesV,
        header: 'Quantum State',
        content:
          'The Quantum State tab shows each state superposition at the currently previewed time.'
      },
      {
        id: 'tab-Histogram',
        position: tabSidesV,
        header: 'Histogram',
        content:
          'The Histogram shows the probability of each state at the currently previewed time.'
      },
      {
        id: 'tab-Code',
        position: tabSidesV,
        header: 'Code',
        content: 'Code preview with real-time code update.'
      }
    ];
  }, []);

  const setHelpIdx = useCallback(
    (idx: number | null) => {
      setCurrentHelpIdx(idx);
      setHelpStorage({ seen: true });
    },
    [setHelpStorage]
  );

  const currentScenario = useMemo(
    () => (currentHelpIdx !== null ? helpScenarios[currentHelpIdx] : null),
    [currentHelpIdx, helpScenarios]
  );

  // Watch scenario ID and trigger help
  const prevScenarioIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const id = currentScenario?.id;
    if (!id || id === prevScenarioIdRef.current) {
      return;
    }
    prevScenarioIdRef.current = id;
    if (id !== HELP_ID_INIT) {
      help.trigger(id);
    }
  }, [currentScenario?.id, help]);

  // Subscribe to help triggers
  useEffect(() => {
    return help.onTrigger((id) => {
      if (id === HELP_ID_INIT) {
        if (currentHelpIdx === null) {
          setHelpIdx(1);
        } else {
          setHelpIdx(null);
        }
      }
    });
  }, [currentHelpIdx, help, setHelpIdx]);

  const helpTargetElement = useMemo(() => {
    if (!currentScenario) {
      return null;
    }
    return help.getAvailableElement(currentScenario.id);
  }, [currentScenario, help]);

  // Scroll target into view
  const prevTargetRef = useRef<Element | null>(null);
  useEffect(() => {
    if (!helpTargetElement || helpTargetElement === prevTargetRef.current) {
      return;
    }
    prevTargetRef.current = helpTargetElement;
    helpTargetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [helpTargetElement]);

  const [targetRect, setTargetRect] = useState<Rect | null>(null);

  // Track which element targetRect was last measured for.
  // When helpTargetElement changes, this ref lags behind in the same render,
  // so we can detect staleness synchronously without waiting for a useEffect.
  const targetElementRef = useRef<Element | null>(null);
  const targetRectIsStale = targetElementRef.current !== helpTargetElement;

  useRaf(
    () => helpTargetElement != null,
    () => {
      const el = helpTargetElement;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const newRect = {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height
      };
      targetElementRef.current = el;
      setTargetRect((prev) => (isEqual(prev, newRect) ? prev : newRect));
    }
  );

  const showMask =
    targetRect && !!currentScenario && currentScenario.mask !== false;

  const POS_PADDING = 15;
  const MASK_PADDING = 5;

  const effectiveTargetRect = useMemo(
    () => targetRect ?? { x: 0, y: 0, width: 0, height: 0 },
    [targetRect]
  );

  const maskStyle = useMemo(
    () => ({
      left: `${effectiveTargetRect.x - MASK_PADDING}px`,
      top: `${effectiveTargetRect.y - MASK_PADDING}px`,
      width: `${effectiveTargetRect.width + MASK_PADDING * 2}px`,
      height: `${effectiveTargetRect.height + MASK_PADDING * 2}px`
    }),
    [effectiveTargetRect]
  );

  const helpWindowRef = useRef<HTMLDivElement>(null);
  const helpWindowSize = useDOMNodeSize(helpWindowRef);
  const hasMeasuredSize = helpWindowSize.width > 0 || helpWindowSize.height > 0;
  // Only reveal once both the window size AND target position are known.
  // helpTargetElement == null means no target (e.g. welcome step) — no rect needed.
  const isReady =
    hasMeasuredSize &&
    (helpTargetElement == null || (!targetRectIsStale && targetRect != null));
  const vpSize = useWindowInnerSize();

  function positionSide(pos: HelpPosition, refRect: Rect, size: Size): Vec2 {
    switch (pos) {
      case HelpPosition.Top:
        return {
          x: refRect.x + refRect.width / 2 - size.width / 2,
          y: refRect.y - size.height
        };
      case HelpPosition.Bottom:
        return {
          x: refRect.x + refRect.width / 2 - size.width / 2,
          y: refRect.y + refRect.height
        };
      case HelpPosition.Left:
        return {
          x: refRect.x - size.width,
          y: refRect.y + refRect.height / 2 - size.height / 2
        };
      case HelpPosition.Right:
        return {
          x: refRect.x + refRect.width,
          y: refRect.y + refRect.height / 2 - size.height / 2
        };
      default:
        assertNever(pos);
    }
  }

  const windowStyle = useMemo(() => {
    const refRect = effectiveTargetRect;
    const paddedRect = Rect.addMargin(refRect, POS_PADDING);
    const contentSize = helpWindowSize;
    const positions = currentScenario?.position ?? [];
    const vpRect = { x: 0, y: 0, width: vpSize.width, height: vpSize.height };

    let windowPos = {
      x: vpSize.width / 2 - contentSize.width / 2,
      y: vpSize.height / 2 - contentSize.height / 2
    };
    let usedDirection: HelpPosition | null = null;

    const hasMeasuredSize = contentSize.width > 0 || contentSize.height > 0;
    if (hasMeasuredSize) {
      for (const pos of positions) {
        const posVec = positionSide(pos, paddedRect, contentSize);
        const windowRect = Rect.fromPosSize(posVec, contentSize);
        if (Rect.containsRect(vpRect, windowRect)) {
          windowPos = posVec;
          usedDirection = pos;
          break;
        }
      }
    }

    const refCenter = {
      x: refRect.x + refRect.width / 2,
      y: refRect.y + refRect.height / 2
    };
    let tipStyle: CSSProperties = { display: 'none' };

    switch (usedDirection) {
      case HelpPosition.Top:
        tipStyle = { bottom: '0', left: `${refCenter.x - windowPos.x}px` };
        break;
      case HelpPosition.Bottom:
        tipStyle = { top: '0', left: `${refCenter.x - windowPos.x}px` };
        break;
      case HelpPosition.Left:
        tipStyle = { right: '0', top: `${refCenter.y - windowPos.y}px` };
        break;
      case HelpPosition.Right:
        tipStyle = { left: '0', top: `${refCenter.y - windowPos.y}px` };
        break;
    }

    return {
      tip: tipStyle,
      wrapper: {
        left: `${windowPos.x}px`,
        top: `${windowPos.y}px`
      }
    };
  }, [
    effectiveTargetRect,
    helpWindowSize,
    currentScenario?.position,
    vpSize.width,
    vpSize.height
  ]);

  function onClose() {
    setHelpIdx(null);
  }

  function onConfirm() {
    const nextId = (currentHelpIdx ?? 0) + 1;
    if (nextId >= helpScenarios.length) {
      setHelpIdx(null);
    } else {
      setHelpIdx(nextId);
    }
  }

  function onPrevious() {
    const prevId = (currentHelpIdx ?? 1) - 1;
    if (prevId >= 0) {
      setHelpIdx(prevId);
    }
  }

  return (
    <>
      {showMask && <div className="HelpWindow-overlay" />}
      {showMask && <div className="HelpWindow-mask" style={maskStyle} />}
      {currentScenario && (
        <div
          ref={helpWindowRef}
          className={`HelpWindow${isReady ? ' HelpWindow--ready' : ''}`}
          style={windowStyle.wrapper}
        >
          <div className="HelpWindow-tip" style={windowStyle.tip} />
          {currentHelpIdx && (
            <div className="HelpWindow-counter">
              {currentHelpIdx}/{helpScenarios.length - 1}
            </div>
          )}
          <HelpContent
            currentHelpIdx={currentHelpIdx}
            scenario={currentScenario}
            onClose={onClose}
            onConfirm={onConfirm}
            onPrevious={onPrevious}
          />
        </div>
      )}
    </>
  );
}
